import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Box, Drawer, InputAdornment, LinearProgress, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { SearchIcon } from 'components/Icons'
import formatAddress from 'utils/formatAddress'
import { basePath } from '../Tabs'
import AddressChangeList, { fullName } from './List'
import Review from './Review'
import AddressSection from './Forms/AddressSection'
import FilesSection from './Forms/FilesSection'
import PeopleSection from './Forms/PeopleSection'

const SECTIONS = { address: AddressSection, people: PeopleSection, files: FilesSection }

const drawerPaper = { sx: { width: { xs: '100%', sm: 600 } } }
const reviewDrawerPaper = { sx: { width: { xs: '100%', sm: 720 } } }

const searchText = item =>
  [fullName(item.primaryParent), ...(item.students ?? []).map(fullName), formatAddress(item.address)]
    .join(' ')
    .toLowerCase()

export default function AddressChangePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id, section } = useParams()
  const [query, setQuery] = useState('')

  const { list, error } = useSelector(state => state.needsAttention.addressChange)

  const listPath = `${basePath}/address-change`
  const close = () => navigate(listPath)
  const closeSection = () => navigate(`${listPath}/${id}`)

  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const rows =
    list &&
    list
      .filter(item => words.every(word => searchText(item).includes(word)))
  const selected = id && list?.find(item => String(item.id) === String(id))
  const Section = selected?.status === 'pending' ? SECTIONS[section] : null

  return (
    <Box sx={{ pt: 2 }}>
      {error ? (
        <Alert severity={error === 'no-campus' ? 'info' : 'error'}>
          {error === 'no-campus'
            ? t('Select a campus to see its address change requests.')
            : t('Address change requests could not be loaded.')}
        </Alert>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder={t('Search parent, student or address')}
              value={query}
              onChange={event => setQuery(event.target.value)}
              sx={{ flex: 1, maxWidth: { sm: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
              {t('{{count}} request(s)', { count: rows?.length ?? 0 })}
            </Typography>
          </Stack>

          <AddressChangeList rows={rows} onSelect={row => navigate(`${listPath}/${row.id}`)} />
        </>
      )}

      <Drawer PaperProps={reviewDrawerPaper} open={Boolean(id)} anchor="right" onClose={close}>
        {id &&
          (selected ? (
            <Review
              key={selected.id}
              request={selected}
              onClose={close}
              onEditSection={name => navigate(`${listPath}/${selected.id}/${name}`)}
            />
          ) : list ? (
            <Typography sx={{ p: 3 }}>{t('Request not found')}</Typography>
          ) : (
            <LinearProgress />
          ))}
      </Drawer>

      <Drawer PaperProps={drawerPaper} open={Boolean(Section)} anchor="right" onClose={closeSection}>
        {Section && <Section key={`${selected.id}-${section}`} request={selected} onClose={closeSection} />}
      </Drawer>
    </Box>
  )
}
